import React, { createContext, useContext, useEffect, useMemo } from "react";
import { translations, contactInfo as defaultContactInfo } from "../i18n/translations";
import { deepMerge } from "../lib/deepMerge";
import { mergeLangTranslations } from "@/lib/articles";
import { normalizeBranding } from "@/admin/adminPayload";
import { serviceMenu } from "@/i18n/services";
import { useSiteContent } from "./SiteContentContext";

const defaultHomepageSettings = {
  showTestimonials: true,
  showWhyUs: true,
  showPortrait: true,
  showPartners: true,
  portraitScrollSeconds: 48,
  partnersScrollSeconds: 15,
};

function parseScrollSeconds(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.min(120, Math.round(n));
}

const LanguageContext = createContext(null);

const STORAGE_KEY = "sd_lang";

/**
 * Public site is Romanian-only.
 * Admin EN fields / DB `en` payloads are left untouched (not shown publicly).
 */
export function LanguageProvider({ children }) {
  const { payload } = useSiteContent();
  const lang = "ro";

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Drop legacy EN preference so old sessions don't expect dual content.
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved === "en") window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.setItem(STORAGE_KEY, "ro");
    } catch {
      /* ignore */
    }
    document.documentElement.lang = "ro";
  }, []);

  const t = useMemo(
    () => mergeLangTranslations(translations.ro, payload.ro || {}),
    [payload]
  );

  const homepageSettings = useMemo(() => {
    const mergedRo = mergeLangTranslations(translations.ro, payload.ro || {});
    const hs = mergedRo.homepageSettings;
    if (!hs || typeof hs !== "object") return { ...defaultHomepageSettings };
    return {
      showTestimonials: hs.showTestimonials !== false,
      showPortrait: hs.showPortrait !== false,
      showPartners: hs.showPartners !== false,
      portraitScrollSeconds: parseScrollSeconds(
        hs.portraitScrollSeconds,
        defaultHomepageSettings.portraitScrollSeconds
      ),
      partnersScrollSeconds: parseScrollSeconds(
        hs.partnersScrollSeconds,
        defaultHomepageSettings.partnersScrollSeconds
      ),
    };
  }, [payload.ro]);

  const serviceCards = useMemo(() => {
    const cards = t.serviceCards;
    if (Array.isArray(cards) && cards.length > 0) return cards;
    return serviceMenu.ro;
  }, [t]);

  const contactInfo = useMemo(
    () => deepMerge({ ...defaultContactInfo }, payload.contactInfo || {}),
    [payload.contactInfo]
  );

  const branding = useMemo(() => normalizeBranding(payload.branding), [payload.branding]);

  useEffect(() => {
    // Google indexes the first HTML icons. Keep a single .ico so SERP does not
    // pick logo_sd.png / admin PNG uploads (those get cropped into a “blob”).
    const href = "/favicon.ico";
    document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach((link) => {
      link.setAttribute("href", href);
      link.setAttribute("type", "image/x-icon");
      link.setAttribute("sizes", "48x48");
    });
  }, [branding.faviconUrl]);

  const value = useMemo(
    () => ({
      lang,
      // No-ops kept for callers that still expect these APIs.
      setLang: () => {},
      toggleLang: () => {},
      t,
      contactInfo,
      branding,
      homepageSettings,
      serviceCards,
    }),
    [lang, t, contactInfo, branding, homepageSettings, serviceCards]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
